import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import DesignationIcon from './DesignationIcon';
import '../styles/UserDetailsCard.css';

// Social link icons
const SocialLinkIcon = ({ type, size = 14 }) => {
  switch (type) {
    case 'instagram':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
          <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
          <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
        </svg>
      );
    case 'tiktok':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
        </svg>
      );
    case 'youtube':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z"/>
          <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"/>
        </svg>
      );
    case 'website':
    default:
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <circle cx="12" cy="12" r="10"/>
          <line x1="2" y1="12" x2="22" y2="12"/>
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
        </svg>
      );
  }
};

const SOCIAL_LINK_LABELS = {
  tiktok: 'TikTok',
  youtube: 'YouTube',
  instagram: 'Instagram',
  website: 'Website/Blog',
};

/**
 * UserDetailsCard - Displays a user's designation, notes, and social links for a place
 * Used in PlaceDetails for Your Details, Owner's Details, Collaborators, and Connections
 */
function UserDetailsCard({
  user, // { userId, username, displayName, profilePhoto }
  designation, // { badge, avoidReason, notes, socialLinks }
  placeId,
  placeName, // Name of the place (for context when navigating to lists)
  isCurrentUser = false,
  showAvatar = true,
  showMenu = true,
  clickableProfile = true, // Whether avatar/name click navigates to profile
  onEdit, // Called when edit is clicked (for current user)
  onReport, // Called when report is clicked
  canFollow = false,
  canFriend = false,
  isFollowing = false,
  isFriend = false,
  onFollow,
  onUnfollow,
  onFriend,
  onUnfriend,
}) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  const badge = designation?.badge;
  const notes = designation?.notes;
  const avoidReason = designation?.avoidReason;
  const socialLinks = designation?.socialLinks || [];
  const isPrivate = designation?.isPrivate;

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) &&
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [menuOpen]);

  const handleMenuToggle = (e) => {
    e.stopPropagation();
    setMenuOpen(!menuOpen);
  };

  const handleViewProfile = () => {
    setMenuOpen(false);
    if (user.username) {
      navigate(`/user/${user.username}`);
    }
  };

  const handleViewLists = () => {
    setMenuOpen(false);
    if (user.userId && user.username && placeId) {
      navigate(`/user/${user.username}/lists`, { state: { placeId, placeName } });
    }
  };

  const handleFollowToggle = () => {
    setMenuOpen(false);
    if (isFollowing && onUnfollow) {
      onUnfollow(user.userId);
    } else if (!isFollowing && onFollow) {
      onFollow(user.userId);
    }
  };

  const handleFriendToggle = () => {
    setMenuOpen(false);
    if (isFriend && onUnfriend) {
      onUnfriend(user.userId);
    } else if (!isFriend && onFriend) {
      onFriend(user.userId);
    }
  };

  const handleReportContent = () => {
    setMenuOpen(false);
    if (!onReport) return;

    onReport({
      contentType: notes ? 'note' : 'social_link',
      content: notes || '',
      socialLinks: socialLinks,
      reportedUserId: user.userId,
    });
  };

  const handleReportLink = (link) => {
    if (!onReport) return;
    onReport({
      contentType: 'social_link',
      content: `${link.type}: ${link.url}`,
      reportedUserId: user.userId,
    });
  };

  const handleEditClick = () => {
    if (onEdit) {
      onEdit();
    }
  };

  // Get badge info
  const getBadgeLabel = () => {
    const labels = {
      'gem': 'Gem',
      'highly-recommended': 'Highly Recommended',
      'visited': 'Visited',
      'avoid': avoidReason ? `Avoid: ${avoidReason.charAt(0).toUpperCase() + avoidReason.slice(1)}` : 'Avoid',
      'want-to-go': 'Want to Go',
    };
    return labels[badge] || 'Unrated';
  };

  const hasContent = badge || notes || socialLinks.length > 0;

  if (!hasContent) return null;

  // Menu dropdown rendered via portal
  const renderMenu = () => {
    if (!menuOpen || !buttonRef.current) return null;

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const menuStyle = {
      position: 'fixed',
      top: buttonRect.bottom + 4,
      right: window.innerWidth - buttonRect.right,
      zIndex: 10000,
    };

    return createPortal(
      <div className="user-details-menu" ref={menuRef} style={menuStyle}>
        {user.username && (
          <button className="menu-item" onClick={handleViewProfile}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
            View Profile
          </button>
        )}
        {user.username && placeId && (
          <button className="menu-item" onClick={handleViewLists}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <line x1="8" y1="6" x2="21" y2="6"/>
              <line x1="8" y1="12" x2="21" y2="12"/>
              <line x1="8" y1="18" x2="21" y2="18"/>
              <line x1="3" y1="6" x2="3.01" y2="6"/>
              <line x1="3" y1="12" x2="3.01" y2="12"/>
              <line x1="3" y1="18" x2="3.01" y2="18"/>
            </svg>
            See Lists with This Place
          </button>
        )}
        {canFollow && (
          <button className="menu-item" onClick={handleFollowToggle}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              {isFollowing ? (
                <>
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="8.5" cy="7" r="4"/>
                  <line x1="18" y1="8" x2="23" y2="8"/>
                </>
              ) : (
                <>
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="8.5" cy="7" r="4"/>
                  <line x1="20" y1="8" x2="20" y2="14"/>
                  <line x1="17" y1="11" x2="23" y2="11"/>
                </>
              )}
            </svg>
            {isFollowing ? 'Unfollow' : 'Follow'}
          </button>
        )}
        {canFriend && (
          <button className="menu-item" onClick={handleFriendToggle}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
            {isFriend ? 'Unfriend' : 'Add Friend'}
          </button>
        )}
        {(notes || socialLinks.length > 0) && (
          <>
            <div className="menu-divider" />
            <button className="menu-item report" onClick={handleReportContent}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/>
                <line x1="4" y1="22" x2="4" y2="15"/>
              </svg>
              Report Note or Link
            </button>
          </>
        )}
      </div>,
      document.body
    );
  };

  return (
    <div className={`user-details-card ${isCurrentUser ? 'current-user-card' : ''}`}>
      {/* Header row - different for current user vs other users */}
      {isCurrentUser ? (
        /* Current user: show edit button on left, badge on right */
        <div className="user-details-header current-user-header">
          {onEdit && (
            <button className="edit-details-btn" onClick={handleEditClick}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
              <span>Edit Details</span>
            </button>
          )}
          {badge && (
            <div className="user-badge-wrapper">
              {isPrivate && (
                <span className="private-indicator" title="Private - hidden from others">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                </span>
              )}
              <div className={`user-badge ${badge}`}>
                <DesignationIcon designation={badge} size={14} />
                <span className="badge-label">{getBadgeLabel()}</span>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Other users: show avatar, name, badge, then menu */
        <div className="user-details-header">
          {showAvatar && (
            <div
              className={`user-avatar ${clickableProfile && user.username ? 'clickable' : ''}`}
              onClick={clickableProfile && user.username ? handleViewProfile : undefined}
            >
              {user.profilePhoto && user.profilePhoto.trim() !== '' ? (
                <img src={user.profilePhoto} alt={user.displayName || user.username} />
              ) : (
                <div className="avatar-placeholder">
                  {(user.displayName || user.username || '?')[0].toUpperCase()}
                </div>
              )}
            </div>
          )}
          <div className="user-info-section">
            <div
              className={`user-info ${clickableProfile && user.username ? 'clickable' : ''}`}
              onClick={clickableProfile && user.username ? handleViewProfile : undefined}
            >
              <span className="user-name">{user.displayName || user.username}</span>
            </div>
          </div>
          {badge && (
            <div className={`user-badge ${badge}`}>
              <DesignationIcon designation={badge} size={14} />
              <span className="badge-label">{getBadgeLabel()}</span>
            </div>
          )}
          {showMenu && (
            <>
              <button
                ref={buttonRef}
                className="menu-btn"
                onClick={handleMenuToggle}
                aria-label="More options"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <circle cx="12" cy="5" r="2"/>
                  <circle cx="12" cy="12" r="2"/>
                  <circle cx="12" cy="19" r="2"/>
                </svg>
              </button>
              {renderMenu()}
            </>
          )}
        </div>
      )}

      {/* Notes section */}
      {notes && (
        <div className="user-notes-section">
          <p className="user-notes">{notes}</p>
        </div>
      )}

      {/* Social links section */}
      {socialLinks.length > 0 && (
        <div className="user-social-links">
          {socialLinks.map((link, index) => (
            <a
              key={index}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className="social-link"
              onClick={(e) => e.stopPropagation()}
            >
              <SocialLinkIcon type={link.type} size={14} />
              <span>{SOCIAL_LINK_LABELS[link.type] || link.type}</span>
              {!isCurrentUser && onReport && (
                <button
                  className="report-link-btn"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleReportLink(link);
                  }}
                  aria-label="Report link"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/>
                    <line x1="4" y1="22" x2="4" y2="15"/>
                  </svg>
                </button>
              )}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

export default UserDetailsCard;
